/**
 * Fourchettes de prix génériques (matériel + pose), pas rattachées aux tarifs réels
 * de prestataires — à afficher explicitement comme une estimation indicative.
 */

// Barème €/m³ stocké HT, par tranche de volume (m³). En dessous de 3 m³, le tarif de la
// tranche 3–10 m³ est appliqué par extrapolation (pas de tarif dédié en dessous).
export const CUVE_TIERS = [
  { max: 10, ent: [1000, 1200], aer: [500, 700] },
  { max: 20, ent: [800, 1000], aer: [300, 500] },
  { max: 30, ent: [700, 900], aer: [200, 400] },
  { max: 50, ent: [650, 850], aer: [180, 360] },
  { max: Infinity, ent: [650, 850], aer: [160, 340] },
];

export const cuveCost = (type, volL) => {
  const volM3 = volL / 1000;
  const tier = CUVE_TIERS.find(t => volM3 <= t.max) || CUVE_TIERS[CUVE_TIERS.length - 1];
  const [lowRate, highRate] = type === 'aerienne' ? tier.aer : tier.ent;
  return { low: Math.round(lowRate * volM3), high: Math.round(highRate * volM3) };
};

// Coefficients d'affinage terrassement (cuve enterrée uniquement) — ordres de grandeur très
// simplifiés, non sourcés individuellement, cohérents avec le reste du module.
export const ACCES_COEF = { facile: 1.0, etroit: 1.3, tres_difficile: 1.6 };
export const SOL_COEF = { meuble: 1.0, argileux: 1.15, rocheux: 1.35 };
export const TRANCHEE_EUR_PAR_ML = { low: 8, high: 15 };

export const ACCES_OPTIONS = [
  { val: 'facile', label: 'Accès facile', desc: 'Engin standard, pas de contrainte' },
  { val: 'etroit', label: 'Accès étroit (< 2,5 m)', desc: 'Nécessite une micro-pelle' },
  { val: 'tres_difficile', label: 'Accès très difficile', desc: 'Portage manuel, engin limité' },
];

export const SOL_OPTIONS = [
  { val: 'meuble', label: 'Sol meuble', desc: 'Terre, sable — creusement facile' },
  { val: 'argileux', label: 'Sol argileux', desc: 'Terrassement plus lent' },
  { val: 'rocheux', label: 'Sol rocheux', desc: 'Peut nécessiter un brise-roche' },
];

// Main-d'œuvre d'installation/raccordement de la cuve elle-même (positionnement, raccords
// entrée/sortie, étanchéité) — distincte du terrassement (excavation, ci-dessus), qui reste
// facturé séparément pour une cuve enterrée. Les deux postes sont complémentaires, pas redondants.
export const mainOeuvreCost = (m) => {
  if (m === 'diy') return { low: 0, high: 0 };
  return { low: 600, high: 1200 };
};

/**
 * @param {Object} cuveState - { type: 'aerienne'|'enterree', volume (L), mainoeuvre, refined,
 *   acces, sol, distanceMaison (ml) }
 */
export const computeCuveCost = (cuveState) => {
  const base = cuveCost(cuveState.type, cuveState.volume);
  const breakdown = [{
    name: `Cuve (${cuveState.volume.toLocaleString('fr-FR')} L, ${cuveState.type === 'aerienne' ? 'aérienne' : 'enterrée'})`,
    low: base.low,
    high: base.high,
  }];

  let low = base.low, high = base.high;

  if (cuveState.type === 'enterree' && cuveState.refined) {
    const accesCoef = ACCES_COEF[cuveState.acces] ?? 1;
    const solCoef = SOL_COEF[cuveState.sol] ?? 1;
    const coef = accesCoef * solCoef;
    if (coef !== 1) {
      const surcoutLow = Math.round(base.low * (coef - 1));
      const surcoutHigh = Math.round(base.high * (coef - 1));
      if (surcoutLow !== 0 || surcoutHigh !== 0) {
        breakdown.push({ name: 'Sujétions de terrassement (accès, sol)', low: surcoutLow, high: surcoutHigh });
        low += surcoutLow;
        high += surcoutHigh;
      }
    }

    const distance = cuveState.distanceMaison || 0;
    if (distance > 0) {
      const tLow = Math.round(distance * TRANCHEE_EUR_PAR_ML.low);
      const tHigh = Math.round(distance * TRANCHEE_EUR_PAR_ML.high);
      breakdown.push({ name: `Tranchée cuve → local technique (${distance} ml)`, low: tLow, high: tHigh });
      low += tLow;
      high += tHigh;
    }
  }

  const main = mainOeuvreCost(cuveState.mainoeuvre);
  if (main.high > 0) {
    breakdown.push({ name: 'Main-d\'œuvre — installation de la cuve', low: main.low, high: main.high });
    low += main.low;
    high += main.high;
  }

  return { breakdown, low, high };
};

/** Texte court pour l'en-tête replié de la section Cuve. */
export const summarizeCuve = (cuveState) => {
  const base = `${cuveState.type === 'aerienne' ? 'Aérienne' : 'Enterrée'}, ${cuveState.volume.toLocaleString('fr-FR')} L`;
  return cuveState.mainoeuvre === 'pro' ? `${base} · pose pro` : base;
};
